import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Modal } from "@mui/material";
import { formatActionError } from "../../../utils/fileUploadHelpers";
import { LongActionContext, actionFailure } from "./useLongAction";
import ActionPanel from "./ActionPanel";

const asAnswer = (answer) => typeof answer === "string" ? { message: answer } : answer;

export default function LongActionProvider({ children }) {
  const [panel, setPanel] = useState(null);
  const running = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const showAnswer = useCallback((answer) => {
    if (!mounted.current) return;
    const next = asAnswer(answer);
    setPanel(next ? { label: "Done", status: "success", ...next, working: false } : null);
  }, []);

  const run = useCallback(async ({
    label = "Working…", task, success = () => "Done.",
    failure = (error) => formatActionError(error),
  }) => {
    if (running.current) return undefined;
    running.current = true;
    if (mounted.current) {
      setPanel({ working: true, label });
    }
    try {
      const result = await task();
      showAnswer(typeof success === "function" ? success(result) : success);
      return result;
    } catch (error) {
      showAnswer(actionFailure(error, failure));
      throw error;
    } finally {
      running.current = false;
    }
  }, [showAnswer]);

  const value = useMemo(() => ({ enabled: true, run, showAnswer }), [run, showAnswer]);
  return (
    <LongActionContext.Provider value={value}>
      {children}
      <Modal open={Boolean(panel)} disableEscapeKeyDown
        sx={{ zIndex: 13000 }} aria-label="Action result">
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: "min(600px, calc(100% - 32px))", outline: 0 }} tabIndex={-1}>
          {panel && <ActionPanel {...panel}
            onClose={() => {
              if (panel.working) return;
              setPanel(null);
              panel.onDone?.();
            }} />}
        </Box>
      </Modal>
    </LongActionContext.Provider>
  );
}
